import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useChatStore } from '../../store/chatStore';
import { useSendMessage, useClearChat } from '../../api/chat';
import ChatMessage from './ChatMessage';

// ── Context-aware query suggestions ──

const CONTEXT_QUERIES: Record<string, string[]> = {
  '/graph': [
    'Quels sont les noeuds les plus connectes ?',
    'Detecte les communautes suspectes',
    'Quelles entites forment un cluster ?',
    'Montre les chemins entre [A] et [B]',
  ],
  '/osint': [
    'Quelles nouvelles sources ont ete detectees ?',
    'Resume les dernieres collectes OSINT',
    'Y a-t-il des mentions recentes de [entity] ?',
    'Quels domaines sont lies a [entity] ?',
  ],
  '/alerts': [
    'Quelles alertes critiques sont ouvertes ?',
    'Resume les anomalies cette semaine',
    'Quelles entites declenchent le plus d\'alertes ?',
    'Y a-t-il des patterns dans les alertes recentes ?',
  ],
  '/entities': [
    'Qui est lie a [entity] ?',
    'Quel est le score de risque de [entity] ?',
    'Resume le reseau de [entity]',
    'Quelles entites ont le plus de relations ?',
  ],
  default: [
    'Qui est lie a [entity] ?',
    'Quelles anomalies cette semaine ?',
    'Resume le reseau de [entity]',
    'Quel est le score de risque de [entity] ?',
  ],
};

const CONTEXT_LABELS: Record<string, string> = {
  '/graph': 'Graph Explorer',
  '/osint': 'OSINT',
  '/alerts': 'Alertes',
  '/entities': 'Entites',
  '/dashboard': 'Dashboard',
  '/': 'Dashboard',
};

const QUICK_ACTIONS = [
  { label: 'Risques', icon: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z', query: 'Quelles sont les entites a haut risque actuellement ?' },
  { label: 'Connexions', icon: 'M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5', query: 'Quelles sont les connexions les plus suspectes dans le graphe ?' },
  { label: 'Timeline', icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', query: 'Montre la timeline des evenements recents' },
  { label: 'Sources', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z', query: 'Quelles sources ont fourni des donnees recemment ?' },
];

// ── Simple markdown renderer ──

function renderMarkdown(text: string): string {
  let html = text
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre class="bg-surface-tertiary rounded-lg px-3 py-2 my-2 text-xs overflow-x-auto"><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-surface-tertiary px-1.5 py-0.5 rounded text-xs text-cyber-cyan">$1</code>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-100">$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-sm">$1</li>')
    // URLs
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-cyber-cyan underline hover:text-cyan-300 break-all">$1</a>')
    // Line breaks
    .replace(/\n/g, '<br/>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li[^>]*>.*?<\/li><br\/>?)+)/g, '<ul class="my-2 space-y-1">$1</ul>');

  return DOMPurify.sanitize(html);
}

// ── Entity chip extraction ──

function extractEntityMentions(text: string): string[] {
  const patterns = [
    /\[([A-Z][a-z]+(?:\s[A-Z][a-z]+)+)\]/g,          // [John Doe]
    /entite?\s+"([^"]+)"/gi,                            // entite "Name"
    /\*\*([A-Z][a-zA-Z\s]+(?:Corp|Ltd|Inc|SA|SAS|SARL))\*\*/g, // **Company Corp**
  ];
  const entities = new Set<string>();
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      entities.add(match[1]);
    }
  }
  return Array.from(entities);
}

export default function AIChatPanel() {
  const isOpen = useChatStore((s) => s.isOpen);
  const closeChat = useChatStore((s) => s.closeChat);
  const messages = useChatStore((s) => s.messages);
  const isLoading = useChatStore((s) => s.isLoading);
  const clearChat = useClearChat();
  const sendMessage = useSendMessage();
  const location = useLocation();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // ── Determine context ──

  const currentPath = location.pathname;
  const contextLabel =
    CONTEXT_LABELS[currentPath] ||
    Object.entries(CONTEXT_LABELS).find(([k]) => currentPath.startsWith(k) && k !== '/')?.[1] ||
    'General';

  const contextQueries =
    CONTEXT_QUERIES[currentPath] ||
    Object.entries(CONTEXT_QUERIES).find(([k]) => currentPath.startsWith(k) && k !== 'default')?.[1] ||
    CONTEXT_QUERIES.default;

  // Auto-scroll vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input quand le panel s'ouvre
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput('');
    sendMessage.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExampleClick = (query: string) => {
    setInput(query);
    inputRef.current?.focus();
  };

  const handleQuickAction = (query: string) => {
    setInput(query);
    inputRef.current?.focus();
  };

  const handleCopyMessage = useCallback(async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  const handleEntityClick = (entity: string) => {
    navigate(`/entities?search=${encodeURIComponent(entity)}`);
    closeChat();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 transition-opacity"
          onClick={closeChat}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col bg-surface-primary border-l border-border-subtle shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-surface-secondary">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyber-cyan to-helios-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-100">Xawio IA</h2>
              <p className="text-xs text-gray-500">Assistant d'investigation</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Context indicator */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/20 mr-1">
              <div className="w-1.5 h-1.5 rounded-full bg-cyber-cyan" />
              <span className="text-[10px] font-medium text-cyber-cyan">{contextLabel}</span>
            </div>
            <button
              onClick={clearChat}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-tertiary transition-colors"
              title="Effacer l'historique"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
            <button
              onClick={closeChat}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-tertiary transition-colors"
              title="Fermer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyber-cyan/20 to-helios-600/20 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-200 mb-2">
                Posez une question
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Interrogez Xawio sur vos entites, alertes ou investigations.
              </p>
              <div className="space-y-2 w-full">
                {contextQueries.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleExampleClick(q)}
                    className="w-full text-left px-4 py-2.5 rounded-lg border border-border-subtle bg-surface-secondary hover:bg-surface-tertiary text-sm text-gray-400 hover:text-gray-200 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div key={msg.id} className="group relative">
                  <ChatMessage message={msg} />

                  {/* Copy button + entity chips for assistant messages */}
                  {msg.role === 'assistant' && msg.content && (
                    <div className="mt-1.5 space-y-2">
                      {/* Rendered markdown content */}
                      {msg.content.includes('**') || msg.content.includes('```') || msg.content.includes('- ') ? (
                        <div
                          className="text-sm text-gray-300 leading-relaxed ml-11"
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                        />
                      ) : null}

                      {/* Entity chips */}
                      {extractEntityMentions(msg.content).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 ml-11">
                          {extractEntityMentions(msg.content).map((entity) => (
                            <button
                              key={entity}
                              onClick={() => handleEntityClick(entity)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/20 text-xs font-medium text-cyber-cyan hover:bg-cyber-cyan/20 transition-colors cursor-pointer"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.54a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L5.25 8.81" />
                              </svg>
                              {entity}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Copy button */}
                      <div className="ml-11 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleCopyMessage(msg.id, msg.content)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-gray-500 hover:text-gray-300 hover:bg-surface-tertiary transition-colors"
                        >
                          {copiedId === msg.id ? (
                            <>
                              <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                              <span className="text-green-400">Copie</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                              </svg>
                              <span>Copier</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Indicateur de chargement */}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyber-cyan to-helios-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                    </svg>
                  </div>
                  <div className="bg-surface-tertiary rounded-2xl rounded-tl-sm px-4 py-3 border border-border-subtle">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-cyber-cyan rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-cyber-cyan rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-cyber-cyan rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <div className="px-4 py-2 border-t border-border-subtle/50 bg-surface-secondary/50">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.label}
                onClick={() => handleQuickAction(action.query)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-subtle bg-surface-tertiary hover:bg-surface-primary text-xs font-medium text-gray-400 hover:text-gray-200 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
                </svg>
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-border-subtle bg-surface-secondary p-4">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Posez votre question..."
              rows={1}
              className="flex-1 resize-none rounded-xl bg-surface-tertiary border border-border-subtle px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50 focus:ring-1 focus:ring-cyber-cyan/25 max-h-32"
              style={{ minHeight: '42px' }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-cyber-cyan text-surface-primary hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              title="Envoyer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
